import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import SuperAdminContext from "../../context/SuperAdminContext";
import { RolePermission, UserProps } from "../../interfaces/interfaces";
import { CardComponent, ColumnComponent } from "../../components/ui";

const roleOptions: { label: string; value: RolePermission }[] = [
  { label: "Super Admin", value: "SUPER_ADMIN" },
  { label: "Admin", value: "ADMIN" },
  { label: "User", value: "USER" },
  { label: "Client", value: "CLIENT" },
  { label: "Demo", value: "DEMO" },
];

interface UserFormValues {
  name: string;
  username: string;
  role: RolePermission;
  clientIds: string[];
  active: boolean;
}

interface UserFilterValues {
  clientId?: string;
  search?: string;
}

const cleanFilters = (filters: UserFilterValues = {}) => ({
  clientId: filters.clientId || undefined,
  search: filters.search?.trim() || undefined,
});

export const SuperAdminUsersPage = () => {
  const {
    companies,
    users,
    superAdminLoading,
    getCompanies,
    getUsers,
    createUser,
    updateUser,
    removeUserFromCompany,
  } = useContext(SuperAdminContext);
  const [form] = Form.useForm<UserFormValues>();
  const [filterForm] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProps | null>(null);
  const [companyToRemove, setCompanyToRemove] = useState<string>("");

  useEffect(() => {
    getCompanies();
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyOptions = useMemo(
    () => companies.map((company) => ({ label: company.name, value: company._id })),
    [companies],
  );

  const openCreate = () => {
    setSelectedUser(null);
    form.setFieldsValue({
      name: "",
      username: "",
      role: "USER",
      clientIds: [],
      active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (user: UserProps) => {
    setSelectedUser(user);
    form.setFieldsValue({
      name: user.name,
      username: user.username || user.email,
      role: user.role?.permissions || "USER",
      clientIds: user.clientList?.map((company) => company._id) || [],
      active: user.active,
    });
    setModalOpen(true);
  };

  const refresh = () => {
    const filters = filterForm.getFieldsValue() as UserFilterValues;
    getUsers(cleanFilters(filters));
  };

  const applyFilters = (overrides: UserFilterValues = {}) => {
    const filters = filterForm.getFieldsValue() as UserFilterValues;
    getUsers(cleanFilters({ ...filters, ...overrides }));
  };

  const submitUser = (values: UserFormValues) => {
    const next = () => {
      setModalOpen(false);
      refresh();
    };
    if (selectedUser) {
      updateUser(selectedUser._id, values, next);
    } else {
      createUser(values, next);
    }
  };

  const columns: ColumnsType<UserProps> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 145,
      className: "super-admin__text-cell",
      render: (value, user) => (
        <Button type="link" className="super-admin__link" onClick={() => openEdit(user)}>
          {value || "Unnamed user"}
        </Button>
      ),
    },
    {
      title: "Email",
      dataIndex: "username",
      key: "username",
      width: 220,
      className: "super-admin__text-cell",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 115,
      render: (role) => <Tag color={role?.permissions === "SUPER_ADMIN" ? "blue" : "default"}>{role?.description || role?.permissions}</Tag>,
    },
    {
      title: "Companies",
      dataIndex: "clientList",
      key: "clientList",
      width: 320,
      className: "super-admin__companies-cell",
      render: (clientList) => (
        <Space className="super-admin__companies-list" size={[4, 4]} wrap>
          {clientList?.length
            ? clientList.map((company: any) => (
                <Tag
                  className="super-admin__company-tag"
                  key={company._id}
                  color={company.active ? "green" : "orange"}
                >
                  {company.name}
                </Tag>
              ))
            : (
              <Tag className="super-admin__company-tag" color="blue">
                Internal
              </Tag>
            )}
        </Space>
      ),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      align: "center",
      width: 72,
      render: (active) => <Checkbox checked={active} disabled />,
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      width: 170,
      render: (lastLogin) =>
        lastLogin ? dayjs(lastLogin).format("MM/DD/YYYY HH:mm") : "",
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 110,
      render: (_, user) => (
        <Space>
          <Tooltip title="Edit user">
            <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(user)} />
          </Tooltip>
          <Tooltip title="Remove from company">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!user.clientList?.length}
              onClick={() => {
                setSelectedUser(user);
                setCompanyToRemove(user.clientList?.[0]?._id || "");
                setRemoveModalOpen(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="super-admin">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardComponent
            className="summary-client__card"
            title="Super Admin Users"
            extra={
              <Form form={filterForm} layout="inline" className="super-admin__filters">
                <Form.Item name="clientId">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Company"
                    options={companyOptions}
                    optionFilterProp="label"
                    onChange={(value) => applyFilters({ clientId: value })}
                    style={{ minWidth: 220 }}
                  />
                </Form.Item>
                <Form.Item name="search">
                  <Input.Search
                    allowClear
                    placeholder="Search users"
                    onChange={(event) => {
                      if (!event.target.value) {
                        applyFilters({ search: "" });
                      }
                    }}
                    onSearch={(value) => applyFilters({ search: value })}
                    style={{ minWidth: 220 }}
                  />
                </Form.Item>
                <Button icon={<ReloadOutlined />} onClick={refresh}>
                  Refresh
                </Button>
              </Form>
            }
            actions={[
              <Tooltip title="Create user" placement="left">
                <Button type="link" icon={<UserAddOutlined />} size="large" onClick={openCreate} />
              </Tooltip>,
            ]}
          />
        </ColumnComponent>
        <ColumnComponent>
          <Table
            bordered
            size="small"
            rowKey="_id"
            columns={columns}
            dataSource={users}
            loading={superAdminLoading}
            tableLayout="fixed"
            scroll={{ y: "70vh", x: 1162 }}
          />
        </ColumnComponent>
      </Row>

      <Modal
        open={modalOpen}
        title={selectedUser ? "Edit user" : "Create user"}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submitUser}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="username"
            label="Email"
            rules={[{ required: true }, { type: "email" }]}
          >
            <Input disabled={Boolean(selectedUser)} />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={roleOptions} />
          </Form.Item>
          <Form.Item name="clientIds" label="Companies">
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={companyOptions}
              optionFilterProp="label"
              placeholder="Leave empty for internal super admins"
            />
          </Form.Item>
          <Form.Item name="active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={superAdminLoading}>
            Save
          </Button>
        </Form>
      </Modal>

      <Modal
        open={removeModalOpen}
        title="Remove user from company"
        onCancel={() => setRemoveModalOpen(false)}
        footer={null}
      >
        <Space direction="vertical" className="super-admin__stack">
          <Select
            value={companyToRemove}
            options={selectedUser?.clientList?.map((company) => ({
              label: company.name,
              value: company._id,
            }))}
            onChange={setCompanyToRemove}
            style={{ width: "100%" }}
          />
          <Popconfirm
            title="Remove this user from the selected company?"
            onConfirm={() => {
              if (selectedUser && companyToRemove) {
                removeUserFromCompany(selectedUser._id, companyToRemove, () => {
                  setRemoveModalOpen(false);
                  refresh();
                });
              }
            }}
          >
            <Button danger type="primary" block loading={superAdminLoading}>
              Remove from company
            </Button>
          </Popconfirm>
        </Space>
      </Modal>
    </div>
  );
};
